import React, { useEffect, useState, useCallback } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import SearchBar from "./SearchBar";
import SuggestionList from "./SuggestionList";

const MobileSearchOverlay = ({ onClose }) => {
  const { t } = useTranslation();
  const [overlayResults, setOverlayResults] = useState([]);

  // NEW: store SearchBar's real navigation handler
  const [navigateFromSuggestion, setNavigateFromSuggestion] = useState(null);

  useEffect(() => {
    document.body.classList.add("mobile-search-open");

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.classList.remove("mobile-search-open");
      document.body.style.overflow = prev || "auto";
    };
  }, []);

  // ESC closes
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  // Backdrop close
  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  // Receive results from SearchBar
  const handleResults = useCallback((results) => {
    setOverlayResults(results);
  }, []);

  /**
   * VERY IMPORTANT:
   * This fires BEFORE navigation in SearchBar.
   * So we DO NOT close the overlay here.
   */
  const handleSelect = useCallback(() => {}, []);

  /**
   * FIX:
   * When user clicks an item in SuggestionList, we call the real
   * SearchBar navigation handler (captured via onSuggestionClickRef).
   * After navigation completes, SearchBar triggers onSelect → THEN we close.
   */
  const handleSuggestionClick = useCallback(
    (item) => {
      //console.log('[Overlay] handleSuggestionClick', { item, navigateFromSuggestion });
      if (navigateFromSuggestion) {
        navigateFromSuggestion(item);
      }
    },
    [navigateFromSuggestion]
  );


  return (
    <div
      className="
        fixed inset-0
        bg-black/90 backdrop-blur-md
        flex flex-col
        z-[100000]
      "
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
    >
      {/* HEADER */}
      <div
        className="
          sticky top-0
          flex items-center
          w-full min-w-0
          px-3 py-2
          border-b border-white/10
          bg-black/80 backdrop-blur-md
          z-[100001]
        "
      >
        <button
          onClick={onClose}
          className="
            p-2 mr-2
            rounded-lg
            hover:bg-white/10
            flex-shrink-0
            transition-colors
            focus:outline-none focus:ring-2 focus:ring-white/20
          "
        >
          <X className="w-6 h-6 text-white" />
        </button>

        {/* SearchBar (overlay mode) */}
        <div className="flex-1 min-w-0 overflow-visible">
          <SearchBar
            autoFocus
            isOverlay={true}
            className="w-full min-w-0"
            onResults={handleResults}
            onSelect={() => {
              // SearchBar triggers this AFTER navigate()
              onClose();
            }}
            onSuggestionClickRef={setNavigateFromSuggestion} // NEW: capture internal handler
          />
        </div>
      </div>

      {/* RESULTS LIST */}
      <div
        className="
          flex-1
          overflow-y-auto overflow-x-hidden
          px-4 py-4
          min-w-0
        "
      >
        {overlayResults.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <p className="text-sm text-gray-400 mb-2">
              {t('nav.searchPrompt')}
            </p>
            <p className="text-xs text-gray-500">
              {t('nav.searchExamplesLong')}
            </p>
          </div>
        ) : (
          <SuggestionList
            suggestions={overlayResults}
            isOverlay={true}
            onSuggestionClick={handleSuggestionClick} // REAL navigation call
            onSelect={handleSelect} // DO-NOTHING placeholder (no pre-close)
          />
        )}
      </div>
    </div>
  );
};

export default MobileSearchOverlay;
